import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Tracks which mutations are in flight, so a control can say so and refuse to be
 * fired twice.
 *
 * Every mutation in this portal is a Cloud Function: a cold instance costs about
 * 1.7 seconds before the handler even starts, and the handler then makes several
 * sequential Firestore round trips. Until this existed, a button did nothing
 * visible for that whole time — it stayed enabled, the row stayed on screen, and
 * the only signal was a toast arriving seconds later. People do the reasonable
 * thing with a control that appears not to have worked, which is press it again;
 * the audit log carries four identical failures eleven milliseconds apart from
 * exactly that.
 *
 * Keyed rather than a single boolean because these screens render lists: deleting
 * one sub area must not grey out the delete button of every other row. The key is
 * whatever identifies the target — a document id, or a fixed string for a
 * form-level action there is only one of.
 */
export interface PendingActions {
  /** Whether this particular target is mid-flight. */
  readonly isPending: (key: string) => boolean;
  /** Whether anything at all is mid-flight, for a panel-level control. */
  readonly busy: boolean;
  /** Runs the action unless the same key is already running. */
  readonly run: (key: string, action: () => Promise<void>) => void;
}

export const usePendingActions = (): PendingActions => {
  const [pending, setPending] = useState<readonly string[]>([]);

  /*
   * A ref alongside the state, and not instead of it.
   *
   * The state is what re-renders the button. The ref is what makes the guard
   * correct: two clicks in the same tick both read the same stale `pending`,
   * so a state-only check lets the second one through — which is precisely the
   * double submit this exists to stop.
   */
  const inFlight = useRef<Set<string>>(new Set());

  const run = useCallback((key: string, action: () => Promise<void>): void => {
    if (inFlight.current.has(key)) return;

    inFlight.current.add(key);
    setPending((current) => [...current, key]);

    void (async () => {
      try {
        await action();
      } catch (cause) {
        /*
         * Every caller reports its own failure through a toast, so reaching here
         * means a bug rather than a rejected mutation. It is caught all the same:
         * an escaping rejection would be an unhandled one, and the release below
         * has to run either way — a key left behind would disable its button for
         * the rest of the session.
         */
        console.error(`[pending] la acción "${key}" lanzó sin manejarlo`, cause);
      } finally {
        inFlight.current.delete(key);
        setPending((current) => current.filter((entry) => entry !== key));
      }
    })();
  }, []);

  const isPending = useCallback((key: string): boolean => pending.includes(key), [pending]);

  return useMemo<PendingActions>(
    () => ({ isPending, busy: pending.length > 0, run }),
    [isPending, pending.length, run],
  );
};
